import { NEW_MESSAGES } from '../hubData';

// Contact forms sample messages, shared by the list (ContactForms) and the message page (ContactMessage) so the ids match.
// Text says "49 Metals" / "49metals.com.au"; the pages swap in the client's own name and host with brand().

export type MessageStatus = 'New' | 'Replied' | 'Archived' | 'Spam';
export type FormName = 'Contact us' | 'Investor enquiry';

// what Investi knows about a sender who has signed up on the website
export interface SenderProfile {
    since: string;
    registry: string;
    subscribed: string;
    location: string;
    previous: string;
    questions: number;
}

export interface HistoryItem { kind: 'mail' | 'opened' | 'sent'; text: string; when: string }

export interface ContactMessageItem {
    id: string;
    name: string;
    email: string;
    phone?: string;
    subject: string;
    form: FormName;
    // list column
    received: string;
    // page header ("received today, 9:14 am") and the footer of the message ("Today 9:14 am (AWST)")
    receivedLong: string;
    sentAt: string;
    // the page on the website the form was sent from
    page: string;
    status: MessageStatus;
    body: string;
    profile?: SenderProfile;
    // registered date shown under the message when the sender matched a sign-up
    matched?: string;
    history: HistoryItem[];
    // prefilled reply (otherwise a short generic one)
    reply?: string;
}

const SIGNATURE = 'Kind regards,\nWallace Turner\nManaging Director, 49 Metals';

export const MESSAGES: ContactMessageItem[] = [
    {
        id: 'margaret-chua', name: 'Margaret Chua', email: 'margaret.chua@gmail.com', phone: '+61 412 880 213',
        subject: 'Request for site visit — Gold Mountain', form: 'Contact us', received: '2 hours ago',
        receivedLong: 'today, 9:14 am', sentAt: 'Today 9:14 am (AWST)', page: '/contact-us/', status: 'New',
        body: 'Hi 49 Metals team,\n\nI\'m a shareholder based in Perth and I\'ll be travelling through Nevada in late October. Would it be possible to arrange a short visit to the Gold Mountain project while I\'m there? Happy to fit in with whatever suits the site team.\n\nAlso — is the September investor update still going ahead?\n\nKind regards,\nMargaret',
        profile: { since: '3 Mar 2026', registry: 'Yes · 42,000 shares (HIN ···4421)', subscribed: 'ASX announcements, News', location: 'Perth, WA', previous: '1 (Feb 2026 — replied)', questions: 0 },
        matched: '3 Mar 2026',
        history: [
            { kind: 'mail', text: 'Message received', when: 'Today 9:14 am' },
            { kind: 'opened', text: 'Opened by Wallace Turner', when: 'Today 9:40 am' },
            { kind: 'mail', text: 'Previous: “Dividend reinvestment plan?”', when: '14 Feb 2026 · replied by Tom Bianchi' },
        ],
        reply: `Hi Margaret,\n\nThank you for your message and for your support as a shareholder. We'd be delighted to arrange a visit to Gold Mountain in late October — I've copied our Exploration Manager, Megan Cole, who will be in touch with dates.\n\nYes, the September investor update is going ahead on Tuesday 8 September and you'll receive it by email.\n\n${SIGNATURE}`,
    },
    {
        id: 'dev-patel', name: 'Dev Patel', email: 'dev.patel@outlook.com', phone: '+61 433 102 774',
        subject: 'Question about the placement price', form: 'Investor enquiry', received: '5 hours ago',
        receivedLong: 'today, 6:02 am', sentAt: 'Today 6:02 am (AWST)', page: '/investors/', status: 'New',
        body: 'Hello,\n\nI saw the placement announcement last week. Could you explain how the placement price was set, and whether existing shareholders will be offered an SPP at the same price?\n\nThanks,\nDev',
        profile: { since: '19 Jan 2026', registry: 'Yes · 8,500 shares (SRN ···1187)', subscribed: 'ASX announcements', location: 'Brisbane, QLD', previous: 'None', questions: 2 },
        matched: '19 Jan 2026',
        history: [
            { kind: 'mail', text: 'Message received', when: 'Today 6:02 am' },
        ],
    },
    {
        id: 'liam-wong', name: 'Liam Wong', email: 'liam@wongcapital.com.au',
        subject: 'Media enquiry — podcast interview', form: 'Contact us', received: 'Yesterday',
        receivedLong: 'yesterday, 3:48 pm', sentAt: 'Yesterday 3:48 pm (AWST)', page: '/contact-us/', status: 'New',
        body: 'Hi there,\n\nI host a weekly resources podcast and would love to have your Managing Director on to talk about the Nevada projects. Recording is remote and takes about 40 minutes.\n\nLet me know if that\'s of interest.\n\nCheers,\nLiam',
        history: [
            { kind: 'mail', text: 'Message received', when: 'Yesterday 3:48 pm' },
            { kind: 'opened', text: 'Opened by Sam Taylor', when: 'Yesterday 4:10 pm' },
        ],
    },
    {
        id: 'sarah-oneill', name: 'Sarah O’Neill', email: 'soneill@bigpond.com',
        subject: 'Change of address for dividend statements', form: 'Investor enquiry', received: '2 Sep',
        receivedLong: '2 September, 11:21 am', sentAt: '2 Sep 11:21 am (AWST)', page: '/investors/', status: 'Replied',
        body: 'Hi,\n\nI\'ve moved house — how do I update my address so the statements go to the right place?\n\nSarah',
        profile: { since: '8 Nov 2025', registry: 'Yes · 15,000 shares (HIN ···9032)', subscribed: 'ASX announcements, News', location: 'Fremantle, WA', previous: 'None', questions: 1 },
        matched: '8 Nov 2025',
        history: [
            { kind: 'mail', text: 'Message received', when: '2 Sep 11:21 am' },
            { kind: 'sent', text: 'Replied by Wallace Turner', when: '2 Sep 2:05 pm' },
        ],
    },
    {
        id: 'brendan-taylor', name: 'Brendan Taylor', email: 'brendan.t@gmail.com',
        subject: 'When is the next investor webinar?', form: 'Investor enquiry', received: '1 Sep',
        receivedLong: '1 September, 8:40 am', sentAt: '1 Sep 8:40 am (AWST)', page: '/investors/', status: 'Replied',
        body: 'Hi,\n\nI missed the last webinar. When is the next one, and will there be a recording?\n\nBrendan',
        profile: { since: '3 Sep 2026', registry: 'Not found', subscribed: 'News', location: 'Sydney, NSW', previous: 'None', questions: 0 },
        matched: '3 Sep 2026',
        history: [
            { kind: 'mail', text: 'Message received', when: '1 Sep 8:40 am' },
            { kind: 'sent', text: 'Replied by Wallace Turner', when: '1 Sep 10:12 am' },
        ],
    },
    {
        id: 'james-ridley', name: 'James Ridley', email: 'jridley@minecorp.com',
        subject: 'Supplier introduction — drilling services', form: 'Contact us', received: '30 Aug',
        receivedLong: '30 August, 1:15 pm', sentAt: '30 Aug 1:15 pm (AWST)', page: '/contact-us/', status: 'Archived',
        body: 'Good afternoon,\n\nMinecorp provides RC and diamond drilling across Nevada. I\'d welcome the chance to introduce our team ahead of your next program.\n\nRegards,\nJames Ridley',
        history: [
            { kind: 'mail', text: 'Message received', when: '30 Aug 1:15 pm' },
            { kind: 'opened', text: 'Archived by Sam Taylor', when: '31 Aug 9:00 am' },
        ],
    },
    {
        id: 'anna-novak', name: 'Anna Novak', email: 'anna.novak@proton.me',
        subject: 'Thanks for the quick reply', form: 'Contact us', received: '28 Aug',
        receivedLong: '28 August, 5:30 pm', sentAt: '28 Aug 5:30 pm (AWST)', page: '/contact-us/', status: 'Replied',
        body: 'Thanks for getting back to me so quickly — that answers everything.\n\nAnna',
        history: [
            { kind: 'mail', text: 'Message received', when: '28 Aug 5:30 pm' },
            { kind: 'sent', text: 'Replied by Wallace Turner', when: '29 Aug 8:15 am' },
        ],
    },
    {
        id: 'spam-1', name: 'SEO Growth Team', email: 'rank@seo-boost.example',
        subject: 'Get your website to page 1 of Google', form: 'Contact us', received: '27 Aug',
        receivedLong: '27 August, 2:02 am', sentAt: '27 Aug 2:02 am (AWST)', page: '/contact-us/', status: 'Spam',
        body: 'We guarantee first page rankings in 30 days. Reply for a free audit.',
        history: [{ kind: 'mail', text: 'Message received · blocked as spam', when: '27 Aug 2:02 am' }],
    },
    {
        id: 'spam-2', name: 'Crypto Desk', email: 'offers@coin-desk.example',
        subject: 'Partnership opportunity', form: 'Investor enquiry', received: '25 Aug',
        receivedLong: '25 August, 11:47 pm', sentAt: '25 Aug 11:47 pm (AWST)', page: '/investors/', status: 'Spam',
        body: 'List your token with us today. Limited spots.',
        history: [{ kind: 'mail', text: 'Message received · blocked as spam', when: '25 Aug 11:47 pm' }],
    },
];

// library totals (spec 408:9939) — the sample above is one page of them. "All" is everything except spam.
const TOTALS: Record<MessageStatus, number> = { New: NEW_MESSAGES, Replied: 12, Archived: 2, Spam: 22 };
const SAMPLE = (s: MessageStatus) => MESSAGES.filter(m => m.status === s).length;

// chip counts after local changes: the off-page totals stay, the sample rows move between statuses
export const statusCounts = (rows: ContactMessageItem[]) => {
    const count = (s: MessageStatus) => TOTALS[s] - SAMPLE(s) + rows.filter(m => m.status === s).length;
    const counts = { New: count('New'), Replied: count('Replied'), Archived: count('Archived'), Spam: count('Spam') };
    return { ...counts, All: counts.New + counts.Replied + counts.Archived };
};

// every message ever received (the Export modal's "Everything")
export const ALL_TIME = { count: 214, since: 'Sep 2025' };

export const STATUS_TONE: Record<MessageStatus, 'cyan' | 'green' | 'grey' | 'red'> = { New: 'cyan', Replied: 'green', Archived: 'grey', Spam: 'red' };

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

// sample text names the 49 Metals site; print the client's own
export const brand = (text: string, short: string, host: string) => text.replace(/49metals\.com\.au/g, host).replace(/49 Metals/g, short);
